import * as THREE from 'three';
import { CELLARS, DUNGEON } from '@somnhold/content';
import { simToWorld, WORLD_PER_HEX } from '@somnhold/render';
import { expApart, expRoomAt, hexToWorld, type BattleState, type ExpArea, type ExpDevice, type Expedition, type ExpItem, type ExpWing, type Hero } from '@somnhold/sim';
import { t } from '../i18n.ts';
import { clear, h, setFill, viewH, viewW } from '../ui/dom.ts';
import { ICONS } from '../ui/icons.ts';
import { GoalFold } from './goal-fold.ts';
import { PartyFrames } from './party-frames.ts';

/** A chevron pointing down; turned to point at a place off screen (the crypt's arrows). */
const CHEVRON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 8l7 8 7-8"/></svg>';

const WINGS: ExpWing[] = ['cistern', 'tower', 'powder', 'cells'];

const areaOf = (x: Expedition, i: number): ExpArea | undefined => (i < 0 ? undefined : (Object.keys(x.rooms) as ExpArea[]).find((a) => x.rooms[a] === i));
const doorOpen = (x: Expedition, a: ExpArea, b: ExpArea): boolean => {
  const i = x.links.findIndex(([p, q]) => p === a && q === b);
  return i >= 0 && !!x.open[i];
};
const devAt = (d: ExpDevice): { x: number; y: number } => hexToWorld(d.q, d.r);
const near = (h: Hero, p: { x: number; y: number }, r: number): boolean => (h.x - p.x) ** 2 + (h.y - p.y) ** 2 <= r * r;
const secs = (ticks: number): number => Math.max(0, Math.ceil(ticks / 12));
const clock = (n: number): string => `${Math.floor(n / 60)}:${String(n % 60).padStart(2, '0')}`;

/**
 * What the carry button does right now for this hero: put down what is in hand, take what is at hand (the same
 * reach and rules as the sim's `carry` command), or nothing.
 */
export const carryAction = (s: BattleState, hero: Hero | undefined): 'drop' | 'take' | null => {
  const x = s.dungeon?.exp;
  if (!x || !hero || hero.downTicks > 0 || s.result !== 'running') return null;
  if (hero.carry !== undefined) return 'drop';
  const R = CELLARS.carry.reach;
  if (x.items.some((it) => !it.hooked && it.fuse === 0 && it.carriers.length < (it.kind === 'bell' ? 2 : 1) && near(hero, it, R))) return 'take';
  const stack = x.devices.find((d) => d.kind === 'barrels');
  const rubble = x.devices.find((d) => d.kind === 'rubble');
  if (stack && !rubble?.done && !x.items.some((it) => it.kind === 'barrel') && near(hero, devAt(stack), R)) return 'take';
  return null;
};

/** Where a wing stands: which of its two rooms, and what is left to do there (or what it waits for). */
export const wingStep = (x: Expedition, w: ExpWing): { k: 1 | 2; what: string } | 'done' | null => {
  const lock = x.devices.find((d) => d.kind === 'lock' && d.wing === w);
  if (!lock) return null;
  if (lock.done) return 'done';
  if (w === 'cells') return { k: 1, what: 'cells1' };
  const first = w === 'cistern' ? x.devices.filter((d) => d.kind === 'wheel').every((d) => d.done) : w === 'tower' ? !!x.devices.find((d) => d.kind === 'lever')?.done : !!x.devices.find((d) => d.kind === 'rubble')?.done;
  if (!first) return { k: 1, what: `${w}1` };
  return doorOpen(x, `${w}A` as ExpArea, `${w}B` as ExpArea) ? { k: 2, what: 'lock' } : { k: 1, what: `${w}Wait` };
};

/** A place to point at: something that just opened (for a while), or where the bell has to go. */
type Way = { x: number; y: number; label: string };

/**
 * The garrison cellars' HUD (docs/EXPEDICIONES.md, «Interfaz»): the party; what to do in the room you stand in
 * and where, with the progress of what you are working; at the floodgate, each wing's state; the floodgate's
 * locks, the falls and the ink's countdown; and every consequence said to everyone, with an arrow to it.
 */
export class ExpeditionHud {
  readonly el: HTMLElement;
  readonly party: PartyFrames;
  private goal = h('div.ex-goal', { role: 'status', 'aria-live': 'polite', 'data-test': 'exp-goal' });
  private goalTitle = h('b.ex-title');
  private goalText = h('p.ex-text');
  private barFill = h('i');
  private bar = h('div.dg-goal-bar.ex-bar', {}, this.barFill);
  private wings = h('ul.ex-wings', { 'data-test': 'exp-wings' });
  private locks = h('span.ex-locks', { 'data-test': 'exp-locks' });
  private falls = h('span.ex-falls', { title: t('exp.fallsTip', { s: CELLARS.fallInkTicks / 12 }) });
  private ink = h('span.ex-ink', { title: t('exp.inkTip'), 'data-test': 'exp-ink' });
  private away = h('span.ex-away');
  private status = h('div.ex-status', {}, this.locks, this.falls, this.ink, this.away);
  private ways = h('div.dg-ways');
  private wayEls: HTMLElement[] = [];
  private tags = h('div.dg-tags');
  private tagEls = new Map<string, HTMLElement>();
  /** What just opened, pointed at for a few seconds (wall clock). */
  private pings: (Way & { until: number })[] = [];
  /** The last room stood in (corridors keep its order). */
  private area: ExpArea = 'entry';
  private sig = '';
  private told = new Set<string>();
  /** The panel folded to its title, bar and clocks (a tap on its corner), so the room shows. */
  private fold: GoalFold;

  constructor(
    private s: () => BattleState,
    private me: string,
    private names: Record<string, string>,
    private toast: (msg: string, ms?: number) => void,
  ) {
    this.party = new PartyFrames(me, names);
    this.fold = new GoalFold(this.goal, 'exp-fold');
    this.goal.append(this.fold.el, this.goalTitle, this.goalText, this.bar, this.wings, this.status);
    this.el = h('div.dg-hud.ex-hud', {}, this.ways, this.party.el, this.goal, this.tags);
    this.fold.follow(this.party.el);
  }

  private mine(s: BattleState): Hero | undefined {
    return s.heroes.find((x) => x.pid === this.me);
  }

  private nameOf(hero: Hero): string {
    return this.party.nameOf(hero);
  }

  /** Once per sim tick. */
  update(): void {
    const s = this.s();
    const x = s.dungeon?.exp;
    if (!x) return;
    this.party.update(s);
    const me = this.mine(s);
    const here = areaOf(x, me ? expRoomAt(s, me.x, me.y) : -1);
    if (here) this.area = here;
    const [title, text, progress] = this.objective(s, x, me);
    this.goalTitle.textContent = title;
    this.goalText.textContent = text;
    this.fold.say(text);
    this.bar.hidden = progress === null;
    if (progress !== null) setFill(this.barFill, progress);
    this.goal.classList.toggle('show', s.started);
    this.goal.classList.toggle('urgent', x.gateOpen && !x.raised);
    this.wingList(s, x);
    this.statusLine(s, x);
    this.fallen(s, x);
  }

  /** The room's order: a title (where, and which of the wing's two rooms), what to do and where, and its progress. */
  private objective(s: BattleState, x: Expedition, me: Hero | undefined): [string, string, number | null] {
    const area = this.area;
    const stage = area.endsWith('A') ? ' · 1/2' : area.endsWith('B') ? ' · 2/2' : '';
    const title = `${t(`exp.area.${area}`)}${stage}`;
    if (!me) return [title, '', null];
    const i = s.heroes.indexOf(me);
    if (me.downTicks > 0) return [title, expApart(x) ? t('exp.goal.down', { n: secs(CELLARS.downTicks - (s.tick - (x.downAt[i] ?? s.tick))) }) : t('exp.goal.downTogether'), (me.revive ?? 0) > 0 ? (me.revive ?? 0) / DUNGEON.reviveTicks : null];
    const work = this.working(s, x, me);
    const held: ExpItem | undefined = me.carry === undefined ? undefined : x.items.find((it) => it.id === me.carry);
    if (held?.kind === 'barrel') return [t('exp.area.powderA'), t('exp.goal.barrelHeld'), null];
    if (held?.kind === 'key') return [t('exp.area.cells'), t('exp.goal.keyHeld'), null];
    const bell = x.items.find((it) => it.kind === 'bell');
    if (x.gateOpen) {
      const winch = x.devices.find((d) => d.kind === 'winch');
      if (bell?.hooked) return [t('exp.area.winch'), t('exp.goal.winch'), winch ? winch.hold / winch.need : null];
      if (held?.kind === 'bell') return [t('exp.area.cistern'), t(held.carriers.length < 2 ? 'exp.goal.bellOne' : 'exp.goal.bellMove'), null];
      return [t('exp.area.cistern'), t('exp.goal.bell'), null];
    }
    const lock = (w: ExpWing): [string, string, number | null] => {
      const d = x.devices.find((o) => o.kind === 'lock' && o.wing === w);
      return d?.done ? [title, t('exp.goal.lockDone'), null] : [title, t('exp.goal.lock'), work];
    };
    switch (area) {
      case 'entry':
        return [title, t('exp.goal.entry'), null];
      case 'guard':
        return [title, t('exp.goal.guard'), null];
      case 'hub':
        return [title, t('exp.goal.split'), null];
      case 'cisternA': {
        const wheels = x.devices.filter((d) => d.kind === 'wheel');
        const k = wheels.filter((d) => d.done).length;
        if (k < wheels.length) return [title, t('exp.goal.wheels', { k, n: wheels.length }), work];
        return [title, t(doorOpen(x, 'cisternA', 'cisternB') ? 'exp.goal.cisternGo' : 'exp.goal.cisternWait'), null];
      }
      case 'towerA': {
        if (!x.devices.find((d) => d.kind === 'lever')?.done) return [title, t('exp.goal.lever'), work];
        return [title, t(x.devices.find((d) => d.kind === 'rubble')?.done ? 'exp.goal.towerGo' : 'exp.goal.rubble'), null];
      }
      case 'powderA': {
        if (!x.devices.find((d) => d.kind === 'rubble')?.done) return [title, t('exp.goal.barrel'), null];
        return [title, t(doorOpen(x, 'powderA', 'powderB') ? 'exp.goal.powderGo' : 'exp.goal.powderWait'), null];
      }
      case 'cisternB':
        return lock('cistern');
      case 'towerB':
        return lock('tower');
      case 'powderB':
        return lock('powder');
      case 'cells': {
        const d = x.devices.find((o) => o.kind === 'lock' && o.wing === 'cells');
        return [title, t(d?.done ? 'exp.goal.lockDone' : 'exp.goal.key'), null];
      }
      default:
        return [t('exp.area.cistern'), t('exp.goal.bell'), null];
    }
  }

  /** How far along the wheel, lever, lock or winch next to me is (null: none at hand). */
  private working(s: BattleState, x: Expedition, me: Hero): number | null {
    const bell = x.items.find((it) => it.kind === 'bell');
    for (const d of x.devices) {
      if (d.done || d.need === 0 || (d.kind === 'winch' && !bell?.hooked)) continue;
      if (near(me, devAt(d), d.kind === 'winch' ? CELLARS.winch.range : CELLARS.device.range)) return d.hold / d.need;
    }
    return null;
  }

  /** At the floodgate before it opens: one line per wing, with its step and who is in it. */
  private wingList(s: BattleState, x: Expedition): void {
    const show = s.started && !x.gateOpen && this.area === 'hub';
    this.wings.hidden = !show;
    if (!show) return;
    const rows: [string, string, boolean][] = [];
    for (const w of WINGS) {
      const st = wingStep(x, w);
      if (!st) continue;
      const rooms: string[] = w === 'cells' ? ['cells'] : [`${w}A`, `${w}B`];
      const inWing = s.heroes.filter((hero) => hero.downTicks === 0 && rooms.includes(areaOf(x, expRoomAt(s, hero.x, hero.y)) ?? '')).map((hero) => this.nameOf(hero));
      const line = st === 'done' ? t('exp.ws.done', { wing: t(`exp.wing.${w}`) }) : t('exp.ws.line', { wing: t(`exp.wing.${w}`), k: st.k, what: t(`exp.ws.${st.what}`) });
      rows.push([line, inWing.length ? t('exp.ws.who', { names: inWing.join(', ') }) : t('exp.ws.nobody'), st === 'done']);
    }
    const sig = JSON.stringify(rows);
    if (sig === this.sig) return;
    this.sig = sig;
    clear(this.wings);
    for (const [line, who, done] of rows) this.wings.append(h(`li${done ? '.done' : ''}`, {}, h('span', { html: done ? ICONS.lock : ICONS.walk }), h('b', {}, line), h('span.small', {}, who)));
  }

  /** The floodgate's locks (one mark per wing), the falls, the ink once the gate is open, and anyone gone. */
  private statusLine(s: BattleState, x: Expedition): void {
    const locks = x.devices.filter((d) => d.kind === 'lock');
    const lsig = locks.map((d) => `${d.wing}${d.done ? 1 : 0}`).join(',');
    if (this.locks.dataset.sig !== lsig) {
      this.locks.dataset.sig = lsig;
      clear(this.locks);
      this.locks.append(h('span.ex-label', {}, t('exp.locks')));
      for (const d of locks) this.locks.append(h(`span.ex-lock${d.done ? '.done' : ''}`, { title: t(`exp.wing.${d.wing ?? 'cistern'}`) }, h('span', { html: ICONS.lock }), t(`exp.wingShort.${d.wing ?? 'cistern'}`)));
    }
    this.falls.innerHTML = `${ICONS.skull}${t('exp.falls', { n: x.falls })}`;
    this.falls.hidden = x.falls === 0;
    const inkOn = x.gateOpen && !x.raised;
    this.ink.hidden = !inkOn;
    if (inkOn) {
      const left = secs(x.inkAt - s.tick);
      this.ink.innerHTML = `${ICONS.drop}${t('exp.ink')} <b>${clock(left)}</b>`;
      this.ink.classList.toggle('low', left <= 60);
    }
    const gone = s.heroes.findIndex((_, i) => (x.goneAt[i] ?? 0) > 0);
    const goneHero = s.heroes[gone];
    this.away.hidden = !goneHero;
    if (goneHero) this.away.textContent = t('exp.away', { name: this.nameOf(goneHero), n: secs(CELLARS.dropGraceTicks - (s.tick - (x.goneAt[gone] ?? s.tick))) });
  }

  /** A word when a friend falls or drops out, once each time. */
  private fallen(s: BattleState, x: Expedition): void {
    s.heroes.forEach((hero, i) => {
      const down = `down:${hero.pid}`;
      if (hero.downTicks > 0 && hero.pid !== this.me && !this.told.has(down)) {
        this.told.add(down);
        this.toast(expApart(x) ? t('exp.ev.fallen', { name: this.nameOf(hero), n: CELLARS.downTicks / 12, s: CELLARS.fallInkTicks / 12 }) : t('exp.ev.fallenTogether', { name: this.nameOf(hero) }), 3600);
      } else if (hero.downTicks === 0) this.told.delete(down);
      const gone = `gone:${hero.pid}`;
      if ((x.goneAt[i] ?? 0) > 0 && !this.told.has(gone)) {
        this.told.add(gone);
        this.toast(t('exp.ev.gone', { name: this.nameOf(hero), n: CELLARS.dropGraceTicks / 12 }), 4000);
      } else if ((x.goneAt[i] ?? 0) === 0) this.told.delete(gone);
    });
  }

  private ping(px: number, py: number, label: string, ms = 9000): void {
    this.pings.push({ x: px, y: py, label, until: performance.now() + ms });
  }

  /**
   * Every tick's events, said to everyone as what they change for the others («the water is down: the powder
   * store's passage can be crossed»), with an arrow to the place for a few seconds.
   */
  events(s: BattleState): void {
    const x = s.dungeon?.exp;
    const L = s.map.dungeon;
    const evs = s._events ?? [];
    if (!x || !L || !evs.length) return;
    const me = this.mine(s);
    const rubbleBoom = evs.some((e) => e.t === 'boom' && e.kind === 'rubble');
    for (const e of evs) {
      if (e.t === 'boom' && e.kind === 'barrel' && !rubbleBoom) this.toast(t('exp.ev.barrelBurst'), 3200);
      if (e.t !== 'skill') continue;
      switch (e.skill) {
        case 'expDoor': {
          const i = L.doors.findIndex((d) => {
            const w = hexToWorld(d.q, d.r);
            return w.x === e.x && w.y === e.y;
          });
          const [a, b] = x.links[i] ?? [];
          const key = a === 'guard' && b === 'hub' ? 'guard' : a === 'hub' && b === 'cistern' ? 'gate' : a?.endsWith('A') && b?.endsWith('B') ? b : null;
          if (!key) break;
          this.toast(t(`exp.ev.${key}`), key === 'gate' ? 5000 : 4200);
          this.ping(e.x, e.y, t(`exp.way.${key}`));
          break;
        }
        case 'expWheel': {
          const wheels = x.devices.filter((d) => d.kind === 'wheel');
          const k = wheels.filter((d) => d.done).length;
          if (k < wheels.length) this.toast(t('exp.ev.wheel', { k, n: wheels.length }), 2800);
          break;
        }
        case 'expLock': {
          // The lock nearest to where it turned (the cells' key is laid by the floodgate).
          const locks = x.devices.filter((d) => d.kind === 'lock');
          let best: ExpDevice | undefined;
          let bd = Infinity;
          for (const d of locks) {
            const w = devAt(d);
            const dd = (w.x - e.x) ** 2 + (w.y - e.y) ** 2;
            if (dd < bd) {
              bd = dd;
              best = d;
            }
          }
          const k = locks.filter((d) => d.done).length;
          // The last one opens the floodgate: its own word says it.
          if (!best || k >= locks.length) break;
          this.toast(t('exp.ev.lock', { wing: t(`exp.wing.${best.wing ?? 'cistern'}`), k, n: locks.length }), 3600);
          break;
        }
        case 'expFuse':
          this.toast(t('exp.ev.fuse', { s: CELLARS.barrel.fuse / 12 }), 2600);
          this.ping(e.x, e.y, t('exp.way.fuse'), (CELLARS.barrel.fuse / 12) * 1000);
          break;
        case 'expHooked':
          this.toast(t('exp.ev.hooked'), 3600);
          break;
        case 'expRaised':
          this.toast(t('exp.ev.raised'), 3000);
          break;
        case 'expPick': {
          const it = me && e.hero === me.id ? x.items.find((o) => o.id === me.carry) : undefined;
          if (it?.kind === 'bell' && it.carriers.length < 2) this.toast(t('exp.goal.bellOne'), 3000);
          break;
        }
        default:
          break;
      }
    }
  }

  /** Where to point now: what just opened, and in the last act the bell or the winch; with a load, where it goes. */
  private targets(s: BattleState): Way[] {
    const x = s.dungeon?.exp;
    const me = this.mine(s);
    if (!x || !me || me.downTicks > 0 || !s.started || s.result !== 'running') return [];
    const now = performance.now();
    this.pings = this.pings.filter((p) => p.until > now);
    const out: Way[] = this.pings.slice(-2);
    const held = me.carry === undefined ? undefined : x.items.find((it) => it.id === me.carry);
    const dev = (kind: ExpDevice['kind'], wing?: ExpWing): ExpDevice | undefined => x.devices.find((d) => d.kind === kind && (!wing || d.wing === wing) && !d.done);
    const to = (d: ExpDevice | undefined, label: string): void => {
      if (d) out.push({ ...devAt(d), label });
    };
    if (held?.kind === 'barrel') to(dev('rubble'), t('exp.way.rubble'));
    else if (held?.kind === 'key') to(dev('lock', 'cells'), t('exp.way.gateLock'));
    const bell = x.items.find((it) => it.kind === 'bell');
    if (x.gateOpen && bell && !x.raised) {
      if (bell.hooked || bell.carriers.length) to(x.devices.find((d) => d.kind === 'winch'), t('exp.way.winch'));
      else out.push({ x: bell.x, y: bell.y, label: t('exp.way.bell') });
    }
    return out;
  }

  /** The arrows on screen: over each place in view, or held at the edge pointing to it. */
  private placeWays(positions: Map<number, THREE.Vector3>, camera: THREE.Camera): void {
    const s = this.s();
    const mine = this.mine(s);
    const mp = mine ? positions.get(mine.id) : undefined;
    const ways = this.targets(s);
    const W = viewW();
    const H = viewH();
    const v = new THREE.Vector3();
    ways.forEach((w, k) => {
      let el = this.wayEls[k];
      if (!el) {
        el = h('div.dg-way', {}, h('span.dg-way-arrow', { html: CHEVRON }), h('span.dg-way-txt'));
        this.ways.append(el);
        this.wayEls[k] = el;
      }
      const p = simToWorld(w.x, w.y);
      // Already there (two hexes or less): no need to point.
      const close = !!mp && Math.hypot(mp.x - p.x, mp.z - p.z) < WORLD_PER_HEX * 2;
      el.hidden = close;
      if (close) return;
      (el.querySelector('.dg-way-txt') as HTMLElement).textContent = w.label;
      v.copy(p).setY(1.4).project(camera);
      const behind = v.z > 1;
      let px = (v.x * 0.5 + 0.5) * W;
      let py = (-v.y * 0.5 + 0.5) * H;
      if (behind) {
        px = W - px;
        py = H - py;
      }
      // Clear of the edges, and below the panels along the top.
      const m = 46;
      const top = W <= 640 ? 200 : 120;
      const inView = !behind && px > m && px < W - m && py > top && py < H - m;
      el.classList.toggle('edge', !inView);
      let turn = 0;
      if (!inView) {
        const cx = W / 2;
        const cy = (top + H - m) / 2;
        const dx = px - cx;
        const dy = py - cy;
        const k2 = Math.min((W / 2 - m) / Math.max(1, Math.abs(dx)), (H - m - top) / 2 / Math.max(1, Math.abs(dy)));
        px = cx + dx * k2;
        py = cy + dy * k2;
        turn = Math.atan2(dy, dx) - Math.PI / 2;
      }
      el.style.transform = `translate(${px.toFixed(1)}px, ${py.toFixed(1)}px) translate(-50%, -50%)`;
      (el.querySelector('.dg-way-arrow') as HTMLElement).style.transform = `rotate(${turn.toFixed(3)}rad)`;
    });
    for (let k = ways.length; k < this.wayEls.length; k++) (this.wayEls[k] as HTMLElement).hidden = true;
  }

  /** Every animation frame: the arrows, and a label over each fallen hero (picked up by a friend, or up at the floodgate soon). */
  place(positions: Map<number, THREE.Vector3>, camera: THREE.Camera): void {
    this.placeWays(positions, camera);
    const s = this.s();
    const x = s.dungeon?.exp;
    const seen = new Set<string>();
    const v = new THREE.Vector3();
    s.heroes.forEach((hero, i) => {
      if (hero.downTicks === 0 || !x) return;
      const p = positions.get(hero.id);
      if (!p) return;
      seen.add(hero.pid);
      let tag = this.tagEls.get(hero.pid);
      if (!tag) {
        tag = h('div.dg-tag', {}, h('span.dg-ring'), h('span.dg-txt'));
        this.tags.append(tag);
        this.tagEls.set(hero.pid, tag);
      }
      const k = Math.min(1, (hero.revive ?? 0) / DUNGEON.reviveTicks);
      const up = secs(CELLARS.downTicks - (s.tick - (x.downAt[i] ?? s.tick)));
      (tag.querySelector('.dg-ring') as HTMLElement).style.setProperty('--p', `${Math.round(k * 100)}%`);
      const apart = expApart(x);
      (tag.querySelector('.dg-txt') as HTMLElement).textContent = k > 0 ? t('dungeon.reviving') : hero.pid === this.me ? (apart ? t('exp.upIn', { n: up }) : t('exp.upTogether')) : apart ? t('exp.reviveHint', { name: this.nameOf(hero), n: up }) : t('exp.reviveTogether', { name: this.nameOf(hero) });
      v.copy(p).setY(p.y + 1.2).project(camera);
      tag.style.transform = `translate(${((v.x * 0.5 + 0.5) * viewW()).toFixed(1)}px, ${((-v.y * 0.5 + 0.5) * viewH()).toFixed(1)}px) translate(-50%, -100%)`;
    });
    for (const [pid, tag] of this.tagEls) {
      if (seen.has(pid)) continue;
      tag.remove();
      this.tagEls.delete(pid);
    }
  }
}
